import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import { useAuth } from "../auth-context";
import { api } from "../api";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";

function displayNameFromEmail(email?: string | null): string {
  if (!email) return "User";
  const local = email.split("@")[0] ?? email;
  return local
    .split(/[.\-_+]/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ") || "User";
}

interface SearchResult {
  kind: "event" | "user" | "policy";
  label: string;
  sub?: string;
  to: string;
}

export function TopBar() {
  const { profile, isDemo } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState<SearchResult[] | null>(null);
  const [searching, setSearching] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  const username = displayNameFromEmail(profile.user?.email);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setResults(null);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (!q.trim() || q.trim().length < 2 || !profile.workspace) { setResults(null); return; }
    const term = q.trim().toLowerCase();
    let cancelled = false;
    setSearching(true);
    (async () => {
      try {
        const wsId = profile.workspace.id;
        const [usersSnap, eventsSnap, policiesSnap] = await Promise.all([
          getDocs(query(collection(db, "users"), where("workspaceId", "==", wsId))),
          getDocs(query(collection(db, "events"), where("workspaceId", "==", wsId))),
          getDocs(query(collection(db, "policyRules"), where("workspaceId", "==", wsId))),
        ]);
        const out: SearchResult[] = [];
        usersSnap.docs.forEach((d) => {
          const u = d.data() as any;
          if (u.email?.toLowerCase().includes(term) || u.name?.toLowerCase().includes(term)) {
            out.push({ kind: "user", label: u.email, sub: u.role, to: "/users" });
          }
        });
        eventsSnap.docs.forEach((d) => {
          const e = d.data() as any;
          if ((e.explanation ?? "").toLowerCase().includes(term) || (e.maskedPreview ?? "").toLowerCase().includes(term)) {
            out.push({ kind: "event", label: e.explanation ?? "Event", sub: new Date(e.createdAt).toLocaleString(), to: "/events" });
          }
        });
        policiesSnap.docs.forEach((d) => {
          const p = d.data() as any;
          if (p.name?.toLowerCase().includes(term) || p.description?.toLowerCase().includes(term)) {
            out.push({ kind: "policy", label: p.name, sub: p.category, to: "/policies" });
          }
        });
        if (!cancelled) setResults(out.slice(0, 12));
      } finally {
        if (!cancelled) setSearching(false);
      }
    })();
    return () => { cancelled = true; };
  }, [q, profile.workspace?.id]);

  return (
    <div className="sg-topbar">
      <div className="sg-topbar-left">
        <span className="sg-topbar-workspace">
          <span className="sg-topbar-ws-label">Workspace</span>
          <span className="sg-topbar-ws-name">{profile.workspace?.name ?? "—"}</span>
        </span>
      </div>

      <div className="sg-topbar-right">
        <div className="sg-topbar-search" ref={searchRef}>
          <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden className="sg-topbar-search-icon">
            <circle cx="7" cy="7" r="5" fill="none" stroke="currentColor" strokeWidth="1.4" />
            <path d="M11 11 L14 14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
          <input
            className="sg-topbar-input"
            placeholder="Search users, events, policies…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {results !== null && (
            <div className="sg-search-results">
              {searching && <div className="sg-search-empty">Searching…</div>}
              {!searching && results.length === 0 && <div className="sg-search-empty">No matches</div>}
              {results.map((r, i) => (
                <button
                  key={i}
                  className="sg-search-item"
                  onClick={() => { setQ(""); setResults(null); navigate(r.to); }}
                >
                  <span className={`sg-search-kind sg-search-kind-${r.kind}`}>{r.kind}</span>
                  <span className="sg-search-label">{r.label}</span>
                  {r.sub && <span className="sg-search-sub">{r.sub}</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="sg-topbar-profile" ref={menuRef}>
          <button
            type="button"
            className="sg-topbar-avatar"
            onClick={() => setMenuOpen((s) => !s)}
            aria-label="Profile menu"
            title={username}
          >
            {username.slice(0, 1).toUpperCase()}
          </button>
          {menuOpen && (
            <div className="sg-topbar-menu">
              <div className="sg-topbar-menu-user">
                <div className="sg-topbar-menu-name">{username}</div>
                <div className="sg-topbar-menu-sub">{profile.workspace?.name ?? ""}</div>
              </div>
              <button className="sg-topbar-menu-item" onClick={() => { setMenuOpen(false); navigate("/settings"); }}>Settings</button>
              <button className="sg-topbar-menu-item" onClick={() => { setMenuOpen(false); navigate("/api-access"); }}>API Access</button>
              {profile.user?.role === "super_admin" && (
                <button
                  className="sg-topbar-menu-item"
                  style={{ color: "var(--sg-blue, #2c4a6a)", fontWeight: 500 }}
                  onClick={() => { setMenuOpen(false); navigate("/admin/growth"); }}
                  title="Internal Growth OS — only visible to you"
                >
                  Growth OS (internal)
                </button>
              )}
              <a
                className="sg-topbar-menu-item"
                href="https://www.ventrin.com/docs/"
                target="_blank"
                rel="noopener"
                onClick={() => setMenuOpen(false)}
                style={{ textDecoration: "none", display: "block" }}
              >
                Documentation ↗
              </a>
              <a
                className="sg-topbar-menu-item"
                href="https://www.ventrin.com/api-guide.html"
                target="_blank"
                rel="noopener"
                onClick={() => setMenuOpen(false)}
                style={{ textDecoration: "none", display: "block" }}
              >
                API Guide ↗
              </a>
              <button className="sg-topbar-menu-item" onClick={async () => {
                setMenuOpen(false);
                // Demo sessions clean up after themselves: delete the
                // throwaway user server-side and drop the session flag so
                // the next demo sign-in replays the onboarding tour.
                if (isDemo) {
                  try { await api.endSelfDemoSession(); } catch { /* non-fatal */ }
                  try { sessionStorage.removeItem("vt_demo_onboarding_seen"); } catch {}
                }
                await signOut(auth);
                navigate("/sign-in");
              }}>Sign Out</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
